import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import store, { IAppState } from "../Store";
import Datastore from "../../communication/Datastore";
import { RecycledItem } from "../../types/system/RecycledItem";
import User from "../../types/core/User";
import File from "../../types/files/File";
import { SystemFileType } from "../../types/enums/SystemFileType";
import { clearFolderContents, reloadFolderContents } from "../files/Folders";
import { clearTimetableDetails } from "../archive/Timetables";
import { clearOrganiserDetails } from "../organiser/Organisers";
import { initialiseAllUserDetails } from "../core/User";

// Define the type for our state store entry
type RecycledState = {
    open: boolean;
    types: RecycledType[];
    data: RecycledItem[];
    loading: boolean;
};

// Define the initial state for our state store entry
const initialState: RecycledState = {
    open: false,
    types: [],
    data: [],
    loading: false,
};

// Create a slice to hold all our reducers
const recycledSlice = createSlice({
    name: "recycled",
    initialState: initialState,
    reducers: {
        setRecycledOpen: (state, action: PayloadAction<boolean>) => {
            state.open = action.payload;
        },
        setRecycledTypes: (state, action: PayloadAction<RecycledType[]>) => {
            state.types = action.payload;
            state.open = action.payload.length > 0;
        },
        removeRecycled: (state, action: PayloadAction<RecycledItem[]>) => {
            const removedIds = action.payload.map(item => item.id);
            state.data = state.data.filter(
                item => !removedIds.includes(item.id),
            );
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return initialState;
            })
            .addCase(getRecycled.pending, state => {
                state.loading = true;
            })
            .addCase(
                getRecycled.fulfilled,
                (state, action: PayloadAction<RecycledItem[]>) => {
                    state.data = action.payload;
                    state.loading = false;
                },
            )
            .addCase(getRecycled.rejected, state => {
                state.loading = false;
            })
            .addCase(restoreFiles.pending, state => {
                state.loading = true;
            })
            .addCase(restoreFiles.fulfilled, state => {
                state.loading = false;
            })
            .addCase(restoreFiles.rejected, state => {
                state.loading = false;
            })
            .addCase(removeFiles.pending, state => {
                state.loading = true;
            })
            .addCase(removeFiles.fulfilled, state => {
                state.loading = false;
            })
            .addCase(removeFiles.rejected, state => {
                state.loading = false;
            });
    },
});

const { setRecycledTypes, removeRecycled } = recycledSlice.actions;
export const { setRecycledOpen } = recycledSlice.actions;

// We have to export the reducer as this is added to the state store
export default recycledSlice.reducer;

export const openRecycled = (types: RecycledType[]) => {
    store.dispatch(setRecycledTypes(types));
    store.dispatch(getRecycled(types));
};

export enum RecycledType {
    Files = "files",
    PublishedTimetables = "publishedTimetables",
    PublishedStudentOptions = "publishedStudentOptions",
    PublishedOrganisers = "publishedOrganisers",
    Published = "published",
    Archived = "archived",
}

const getRecycled = createAsyncThunk(
    "recycled/getRecycled",
    async (types: RecycledType[]): Promise<RecycledItem[]> => {
        await store.dispatch(initialiseAllUserDetails());
        const { user } = store.getState().db;

        const fileTypes = types
            .flatMap(type => {
                switch (type) {
                    case RecycledType.PublishedTimetables:
                        return SystemFileType.TimetableFile;
                    case RecycledType.PublishedStudentOptions:
                        return SystemFileType.StudentOptionsFile;
                    case RecycledType.PublishedOrganisers:
                        return SystemFileType.DailyOrganiserFile;
                    case RecycledType.Published:
                        return [
                            SystemFileType.TimetableFile,
                            SystemFileType.StudentOptionsFile,
                            SystemFileType.DailyOrganiserFile,
                        ];
                    default:
                        return null;
                }
            })
            .filter(type => type !== null) as SystemFileType[];
        const nonFileTypes = types
            .map(type => {
                switch (type) {
                    case RecycledType.PublishedTimetables:
                    case RecycledType.PublishedStudentOptions:
                    case RecycledType.PublishedOrganisers:
                        return RecycledType.Published;
                    default:
                        return type;
                }
            })
            .distinct() as RecycledType[];

        return (
            await Promise.all(
                nonFileTypes.map(type => {
                    switch (type) {
                        case RecycledType.Files:
                            return getRecycledFiles(user.allUsers);
                        case RecycledType.Published:
                            return getRecycledPublishedFiles(
                                fileTypes,
                                user.allUsers,
                            );
                        case RecycledType.Archived:
                            return getRecycledArchivedFiles(user.allUsers);
                        default:
                            return [];
                    }
                }),
            )
        ).flat();
    },
    {
        condition: (_, { getState }) =>
            !(getState() as IAppState).db.recycled.loading,
    },
);

const getRecycledFiles = async (users: User[]): Promise<RecycledItem[]> => {
    const result = await Datastore.getType<File[]>(
        `api/SystemIO/Files/Recycled`,
    );

    return result.map(item => new RecycledItem(item, users));
};

const getRecycledPublishedFiles = async (
    types: SystemFileType[],
    users: User[],
): Promise<RecycledItem[]> => {
    const result = await Datastore.getType<File[]>(
        `api/SystemIO/Files/Published/Recycled`,
    );

    return result
        .filter(item => types.includes(item.fileType))
        .map(item => new RecycledItem(item, users));
};

const getRecycledArchivedFiles = async (
    users: User[],
): Promise<RecycledItem[]> => {
    const result = await Datastore.getType<File[]>(
        `api/SystemIO/Files/Archives/Recycled`,
    );

    return result.map(item => new RecycledItem(item, users));
};

export const restoreFiles = createAsyncThunk(
    "recycled/restoreFiles",
    async (items: RecycledItem[], thunkAPI): Promise<void> => {
        const files = items.map(item => item.raw);

        await Datastore.post(`api/SystemIO/Files/Restore`, files);

        thunkAPI.dispatch(removeRecycled(items));
        thunkAPI.dispatch(setRecycledOpen(false));

        const { types } = store.getState().db.recycled;
        if (types.includes(RecycledType.Files)) {
            clearFolderContents();
        }
        if (types.includes(RecycledType.PublishedOrganisers)) {
            clearOrganiserDetails();
        }
        if (types.includes(RecycledType.Archived)) {
            clearTimetableDetails();
        }
    },
    {
        condition: (_, { getState }) =>
            !(getState() as IAppState).db.recycled.loading,
    },
);

export const removeFiles = createAsyncThunk(
    "recycled/removeFiles",
    async (items: RecycledItem[], thunkAPI): Promise<void> => {
        const files = items.map(item => item.raw);

        await Datastore.post(`api/SystemIO/Files/Delete`, files);

        thunkAPI.dispatch(removeRecycled(items));
        thunkAPI.dispatch(setRecycledOpen(false));
        await reloadFolderContents();
    },
    {
        condition: (_, { getState }) =>
            !(getState() as IAppState).db.recycled.loading,
    },
);
